/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  accountListHorizontalItemTemplate,
  accountListingForSelectAccountTemplate,
  accountSwitchDropdownTemplate,
  accountSwitchWidgetVisualTemplate,
  currencyAccountListItemTemplate,
  exchangeTransactionHistoryItemTemplate,
  multiCurrencyDropdownTemplate,
  multiCurrencyVisualTemplate,
  multiIconQuickLinkTemplate,
  quickLinkTemplate,
  titleTemplate,
  transactionHistoryItemTemplate,
} from '@finxone-platform/shared/default-templates';

import { TransactionHistoryTopRightOption } from '@finxone-platform/shared/enums';
import { FormID } from '@finxone-platform/form-action';
import {
  APP_ZONES,
  AccountStatusType,
  BaseWidgetProperties,
  BorderRadiusKeys,
  BorderStyles,
  ButtonSizeOptions,
  ButtonTypes,
  Constants,
  CurrencyExchangeTransactionType,
  DummyRole,
  ENTITY_TYPE,
  EntityType,
  GridPosition,
  InputCssConfig,
  InputFormatType,
  ModalConfiguration,
  NestedWorkflowWidgetConfiguration,
  Page,
  TransactionContextOptions,
  TransactionType,
  TypographyKeys,
  ViewType,
  WidgetNames,
  WidgetTitles,
  WorkflowWidgetConfig,
  WorkflowWidgetConfigurationAttributes,
  commonPageConfigValue,
  cryptoRandomUUID,
  defaultTableListingConfig,
} from '@finxone-platform/shared/sys-config-types';

import { UUID } from '@finxone-platform/shared/base-types';

import {
  accountsColumnListing,
  baseAccountListingColumns,
} from './account-listing/account-listing-column-config-template';
import { OnboardingNextBackWidgetBuilder } from './example-builder';
import { cardsNestedWidgets } from './nested-widgets/cards/cards';
import { conditionalNestedWidgets } from './nested-widgets/conditional/conditional';
import { otpVerificationNestedWidgets } from './nested-widgets/otp/otp';
import { txHistoryNestedWidgets } from './nested-widgets/transaction-history/transaction-history';
import { defaultInputCssConfig, inputSettings } from './payments/payments.type';
import { transactionColumnListing } from './transaction/transaction-coloumn-configuration';

export const defaultTableHeaderSettingCssConfig = {
  tableHeaderBackgroundColor: 'var(--color-surface)',
  tableHeaderTextColor: 'var(--color-on-background)',
  tableHeaderFontSize: 16,
  tableHeaderFontUnit: 'px',
  tableHeaderFontFamily: 'Mulish',
};

export const defaultTableCheckBoxCssConfig = {
  css_config: {
    backgroundColor: 'var(--color-primary)',
    borderColor: 'var(--color-primary)',
    inactiveBorderColor: 'var(--color-primary)',
    inactiveBackgroundColor: 'var(--color-background)',
    inputTextColor: 'var(--color-background)',
    borderRadiusTopLeft: 5,
    borderRadiusTopRight: 5,
    borderRadiusBottomLeft: 5,
    borderRadiusBottomRight: 5,
    borderStyleUnit: 'px',
    borderWidth: 2,
    iconSize: 24,
    iconSizeUnit: 'px',
  },
};

export const baseTransactionHistoryWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: new UUID().toString(),
  name: WidgetNames.RECENT_TRANSACTION_LIST_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: 'Transaction History',
    widgetConfigDisplayName: WidgetTitles.RECENT_TRANSACTION_LIST_WIDGET,
    nestedWidgets: txHistoryNestedWidgets,
    isRemovable: true,
    attributes: {
      widgetProperties: {
        textContent: ``,
        icon: 'search',
        showEntity: false,
        entityInformation: {
          entity: 'transaction history',
        },
        menuOptions: [
          { label: 'View Detail', value: 'view-detail', isIdNotExist: '' },
          { label: 'Download Receipt', value: 'download-receipt', isIdNotExist: '' },
        ],
        inputFieldSettings: {
          ...inputSettings,
          css_config: {
            inputWidth: 25,
            iconSize: 24,
            iconSizeUnit: 'px',
            inputPosition: 'end',
          } as InputCssConfig,
          label: 'Search',
          type: 'search',
          isIconAllowed: true,
          isAllowed: false,
        },
        dropdownActionSetting: {
          isAllowDropdownAction: false,
          dropdownLabel: '',
          dropdownWidth: 25,
        },
        gestures: {
          panEnabled: false,
        },
      } as BaseWidgetProperties,
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        borderWidth: 1,
        borderColor: 'var(--color-border-color)',
        backGroundColor: 'var(--color-surface)',
        borderStyle: 'solid',
        borderRadiusUnit: 'px',
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
      },
      // Widget specific below
      titleTemplate: titleTemplate,
      pageSize: 5,
      pagingEnabled: true,
      defaultTransactionType: TransactionType.ALL,
      transactionContext: TransactionContextOptions.TRANSACTION,
      topRightConfiguration: {
        option: TransactionHistoryTopRightOption.TRANSACTION_TYPE_SELECTOR,
        transactionTypeSelectorOptions: [
          TransactionType.ALL,
          TransactionType.INCOMING,
          TransactionType.OUTGOING,
        ],
        quickLinkOptions: {
          // Should be rendered using the handlebars templates
          textContent: quickLinkTemplate, // so can change change font weight etc or style as a button
          zone: APP_ZONES.LANDING,
          pageUrl: '',
        },
      },
      transactionItemConfig: {
        imageEnabled: true,
        transactionHistoryItemTemplate: transactionHistoryItemTemplate,
        creditBackgroundImage:
          'PHN2ZyB2aWV3Qm94PSIwIDAgNTYgNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZml0PSIiIGhlaWdodD0iMTAwJSIgICAgIHdpZHRoPSIxMDAlIiBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJ4TWlkWU1pZCBtZWV0IiBmb2N1c2FibGU9ImZhbHNlIj4gICAgIDxyZWN0IHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiIgcng9IjIwIiBmaWxsPSIjRTNFOEYzIj48L3JlY3Q+ICAgICA8cGF0aCBkPSJNMjMgMzNMMzMgMjNNMzMgMjNIMjNNMzMgMjNWMzMiIHN0cm9rZT0iYmxhY2siIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiAgICAgICAgIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjwvcGF0aD4gPC9zdmc+',
        debitBackgroundImage:
          'PHN2ZyB2aWV3Qm94PSIwIDAgNTYgNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZml0PSIiIGhlaWdodD0iMTAwJSIgICAgIHdpZHRoPSIxMDAlIiBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJ4TWlkWU1pZCBtZWV0IiBmb2N1c2FibGU9ImZhbHNlIj4gICAgIDxyZWN0IHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiIgcng9IjIwIiB0cmFuc2Zvcm09Im1hdHJpeCgtMSAwIDAgMSA1NiAwKSIgZmlsbD0iI0ZFQ0RDQSI+PC9yZWN0PiAgICAgPHBhdGggZD0iTTMzIDIzTDIzIDMzTTIzIDMzVjIzTTIzIDMzSDMzIiBzdHJva2U9ImJsYWNrIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgICAgICAgICBzdHJva2UtbGluZWpvaW49InJvdW5kIj48L3BhdGg+IDwvc3ZnPg==', // maybe only svg???
        imageCssProps: {
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          marginUnit: 'px',
        },
      },
      tableGridSetting: {
        showHorizontalGridLines: true,
        showVerticalGridLines: false,
        gridLinesWidth: 1,
      },
      rowSettings: {
        rowBgColor: 'var(--color-surface)',
        oddRowBgColor: 'var(--color-surface)',
        evenRowBgColor: 'var(--color-surface)',
        showErrorDuplicationRowBgColor: false,
      },
      viewType: ViewType.LIST,
      selectedListOption: 'None',
      viewListOption: 'none',
      noRecordsTemplate: '',
      viewPaginationOption: ['05', '10', '20'],
      tableHeaderCustomCss:
        'font-size: 16px; font-weight: 700; color:var(--color-on-surface-muted); padding:15px 10px 15px 24px',
      tableRowCustomCss: `padding: 1.6rem 2.4rem; font-size: 16px; font-weight: 400;`,
      tableToolbarHeaderBgColor: 'var(--color-surface)',
      columns: transactionColumnListing,
      filterPillConfig: {
        isEnabled: true,
        globalStyling: true,
        borderConfig: {
          'border-left-width': '1px',
          'border-right-width': '1px',
          'border-top-width': '1px',
          'border-bottom-width': '1px',
          'border-top-left-radius': '20px',
          'border-bottom-left-radius': '20px',
          'border-top-right-radius': '20px',
          'border-bottom-right-radius': '20px',
          borderRadiusClass: BorderRadiusKeys.NONE,
          borderStyle: BorderStyles.SOLID,
        },
        css_config: {
          ...Constants.defaultBaseCssConfig,
          textAlignment: 'start',
          verticalAlignment: 'center',
          textFont: 'Mulish',
          fontSize: 14,
          shadowBlur: 0,
          shadowSpread: 0,
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          shadowColor: '#000000',
          fontWeight: '400',
          color: 'var(--color-primary)',
          marginTop: 0,
          marginRight: 8,
          marginLeft: 8,
          marginBottom: 0,
          marginUnit: 'px',
          paddingRight: 12,
          paddingLeft: 12,
          paddingUnit: 'px',
          height: 36,
          dimensionUnit: 'px',
        },
        colorConfig: {
          idle: {
            textColor: {
              color: 'var(--color-on-background)',
            },
            labelColor: {
              color: 'var(--color-on-background)',
            },
            'border-color': '#EDEDED',
            'background-color': '#EDEDED',
          },
          active: {
            textColor: {
              color: 'var(--color-primary)',
            },
            labelColor: {
              color: 'var(--color-primary)',
            },
            'border-color': 'var(--color-primary)',
            'background-color': '#E1EAFF',
          },
        },
        textStyleClass: TypographyKeys.LABEL_TEXT,
        iconConfig: {
          icon: 'alert-error-icon',
          css_config: {
            marginLeft: 6,
            marginRight: 10,
            marginUnit: 'px',
            iconSize: 14,
            iconUnit: 'px',
            iconColor: 'var(--color-primary)',
          },
        },
      },
    },
    isWidgetPage: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 15,
    minItemRows: 4,
    resizeEnabled: true,
    dragEnabled: true,
    cols: 15,
    rows: 14,
  },
  widgetIconPath: 'listing',
} as WorkflowWidgetConfig;
export const transactionHistoryWidgetConfig = {
  ...baseTransactionHistoryWidgetConfig,
};

export const exchangetransactionHistoryWidgetConfig: WorkflowWidgetConfig = {
  ...baseTransactionHistoryWidgetConfig,
  widgetConfiguration: {
    ...baseTransactionHistoryWidgetConfig.widgetConfiguration,
    attributes: {
      ...baseTransactionHistoryWidgetConfig.widgetConfiguration.attributes,
      widgetProperties: { textContent: '' },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultBaseCssConfig,
        borderWidth: 1,
        borderColor: 'var(--color-border-color)',
        borderStyle: 'solid',
      },
      titleTemplate: titleTemplate,
      pageSize: 5,
      pagingEnabled: true,
      defaultTransactionType: CurrencyExchangeTransactionType.CURRENCYINCOMING,
      transactionContext: TransactionContextOptions.EXCHANGES,
      topRightConfiguration: {
        option: TransactionHistoryTopRightOption.TRANSACTION_TYPE_SELECTOR,
        transactionTypeSelectorOptions: [
          CurrencyExchangeTransactionType.CURRENCYINCOMING,
          CurrencyExchangeTransactionType.CURRENCYOUTGOING,
        ],
        quickLinkOptions: {
          textContent: quickLinkTemplate,
          zone: APP_ZONES.LANDING,
          pageUrl: '',
        },
      },
      transactionItemConfig: {
        imageEnabled: true,
        transactionHistoryItemTemplate: exchangeTransactionHistoryItemTemplate,
        creditBackgroundImage:
          'PHN2ZyB2aWV3Qm94PSIwIDAgNTYgNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZml0PSIiIGhlaWdodD0iMTAwJSIgICAgIHdpZHRoPSIxMDAlIiBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJ4TWlkWU1pZCBtZWV0IiBmb2N1c2FibGU9ImZhbHNlIj4gICAgIDxyZWN0IHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiIgcng9IjIwIiBmaWxsPSIjRTNFOEYzIj48L3JlY3Q+ICAgICA8cGF0aCBkPSJNMjMgMzNMMzMgMjNNMzMgMjNIMjNNMzMgMjNWMzMiIHN0cm9rZT0iYmxhY2siIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiAgICAgICAgIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjwvcGF0aD4gPC9zdmc+',
        debitBackgroundImage:
          'PHN2ZyB2aWV3Qm94PSIwIDAgNTYgNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZml0PSIiIGhlaWdodD0iMTAwJSIgICAgIHdpZHRoPSIxMDAlIiBwcmVzZXJ2ZUFzcGVjdFJhdGlvPSJ4TWlkWU1pZCBtZWV0IiBmb2N1c2FibGU9ImZhbHNlIj4gICAgIDxyZWN0IHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiIgcng9IjIwIiB0cmFuc2Zvcm09Im1hdHJpeCgtMSAwIDAgMSA1NiAwKSIgZmlsbD0iI0ZFQ0RDQSI+PC9yZWN0PiAgICAgPHBhdGggZD0iTTMzIDIzTDIzIDMzTTIzIDMzVjIzTTIzIDMzSDMzIiBzdHJva2U9ImJsYWNrIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgICAgICAgICBzdHJva2UtbGluZWpvaW49InJvdW5kIj48L3BhdGg+IDwvc3ZnPg==', // maybe only svg???
        imageCssProps: {
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          marginUnit: 'px',
        },
      },
    },
  },
};

export const quickLinkWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.QUICK_LINKS_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: 'Quick Link',
    widgetConfigDisplayName: WidgetTitles.QUICK_LINKS_WIDGET,
    attributes: {
      elementsConfig: [],
      name: Page.QUICK_LINKS_WIDGET,
      type: 'links',
      slug: 'quick_links',
      label: 'Quick Links',
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        textFont: 'Mulish',
        fontSize: 20,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      },
      widgetProperties: {
        textContent: 'Quick Links',
        delayButtonActivation: false,
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 4,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'navigation',
} as WorkflowWidgetConfig;

export const resendLinkWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.RESEND_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: 'Resend Link',
    widgetConfigDisplayName: WidgetTitles.RESEND_WIDGET,
    attributes: {
      elementsConfig: [],
      name: Page.RESEND_WIDGET,
      type: 'links',
      slug: 'resend_links',
      label: 'Resend Links',
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        textFont: 'Mulish',
        fontSize: 20,
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 0,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      },
      widgetProperties: {
        textContent: 'Resend Links',
        globalStyling: true,
        globalStylingClass: `${ButtonTypes.PRIMARY} ${ButtonSizeOptions.FULL_WIDTH}`,
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 4,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'navigation',
} as WorkflowWidgetConfig;

export const switchNavigatorQuickLinkWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.SWITCH_NAVIGATOR_QUICK_LINK,
  widgetConfiguration: {
    widgetDisplayName: 'Switch Navigator Quick Link',
    widgetConfigDisplayName: WidgetTitles.SWITCH_NAVIGATOR_QUICK_LINK,
    attributes: {
      elementsConfig: [],
      name: Page.SWITCH_NAVIGATOR_QUICK_LINK,
      type: 'links',
      slug: 'switch_navigator_quick_links',
      label: 'Switch Navigator Quick Links',
      role: '',
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        textFont: 'Mulish',
        fontSize: 15,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderWidth: 1,
        borderRadiusUnit: 'px',
        borderRadiusTopLeft: 10,
        borderRadiusBottomLeft: 10,
        borderRadiusTopRight: 10,
        borderRadiusBottomRight: 10,
        borderColor: '#384F80',
        paddingUnit: 'px',
        paddingTop: 10,
        paddingLeft: 10,
        paddingRight: 10,
        paddingBottom: 10,
        color: '#fff',
        backGroundColor: '#384F80',
        disabledBgColor: '#fff',
        disabledTextColor: '#384F80',
      },
      widgetProperties: {
        textContent: '',
      },
      linksList: [
        {
          textContent: 'Switch',
          pageIndex: 0,
          zone: '',
        },
        {
          textContent: 'Switch',
          pageIndex: 1,
          zone: '',
        },
      ],
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 5,
    minItemCols: 1,
    minItemRows: 1,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'switch-nav',
} as WorkflowWidgetConfig;

export const multiIconQuickLinkWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.MULTI_ICON_QUICK_LINK_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.MULTI_ICON_QUICK_LINK_WIDGET,
    widgetConfigDisplayName: 'Multi Icon',
    attributes: {
      elementsConfig: [],
      name: Page.MULTI_ICON_QUICK_LINK_WIDGET,
      type: 'links',
      slug: 'quick_links_icon',
      label: 'Quick Links Icons',
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'left',
        verticalAlignment: 'center',
        textFont: 'Metropolis',
        fontSize: 20,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        backGroundColor: '#fff',
        color: '#384F80',
      },
      widgetProperties: {
        textContent: '',
      },
      pageIndex: 0,
      zone: '',
      iconConfig: {
        leftIcon: '',
        rightIcon: '',
      },
      itemConfig: {
        itemTemplate: multiIconQuickLinkTemplate,
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 1,
    minItemCols: 1,
    minItemRows: 1,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'generic-widget',
} as WorkflowWidgetConfig;

const buttonGridPosition = {
  x: 0,
  y: 0,
  minItemCols: 1,
  minItemRows: 1,
  rows: 4,
  cols: 6,
  resizeEnabled: true,
  dragEnabled: true,
};

const buttonCssConfig = {
  ...Constants.defaultBaseCssConfig,
  textAlignment: 'center',
  verticalAlignment: 'center',
  shadowBlur: 0,
  shadowSpread: 0,
  shadowOffsetX: 0,
  shadowOffsetY: 0,
  iconSize: 24,
};
export const backButtonWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.BACK_BUTTON_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: 'Back Navigation Button',
    widgetConfigDisplayName: WidgetTitles.BACK_BUTTON_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.BACK_BUTTON_WIDGET,
      type: 'button',
      slug: 'back_button',
      label: 'Back Navigation Button',
      css_config: buttonCssConfig,
      widgetProperties: {
        textContent: '',
        icon: 'arrow-left',
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: buttonGridPosition,
  widgetIconPath: 'forward-back-navigation',
} as WorkflowWidgetConfig;

export const otpVerificationWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.OTP_VERIFICATION,
  name: WidgetNames.OTP_VERIFICATION,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.OTP_VERIFICATION,
    widgetConfigDisplayName: WidgetTitles.OTP_VERIFICATION,
    nestedWidgets: otpVerificationNestedWidgets,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.OTP_VERIFICATION,
      label: WidgetTitles.OTP_VERIFICATION,
      css_config: Constants.defaultBaseCssConfig,
      widgetProperties: {
        textContent: '',
        widgetContext: '',
        inputFieldSettings: {
          css_config: {
            ...defaultInputCssConfig,
            inputHeight: 39,
            inputWidth: 20,
          },
          label: '',
        },
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 1,
    minItemCols: 1,
    minItemRows: 1,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'generic-widget',
} as WorkflowWidgetConfig;

export const forwardButtonWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.FORWARD_BUTTON_WIDGET,
  name: WidgetNames.FORWARD_BUTTON_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: 'Forward Navigation Button',
    widgetConfigDisplayName: WidgetTitles.FORWARD_BUTTON_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.FORWARD_BUTTON_WIDGET,
      type: 'button',
      slug: 'forward_button',
      label: 'Forward Navigation Button',
      css_config: buttonCssConfig,
      widgetProperties: {
        textContent: '',
        icon: 'arrow-right',
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: buttonGridPosition,
  widgetIconPath: 'forward-back-navigation',
} as WorkflowWidgetConfig;

export const lottieAnimationWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.LOTTIE_ANIMATION,
  name: WidgetNames.LOTTIE_ANIMATION,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.LOTTIE_ANIMATION,
    widgetConfigDisplayName: WidgetTitles.LOTTIE_ANIMATION,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.LOTTIE_ANIMATION,
      type: 'animation',
      slug: 'lottie_animation',
      label: WidgetTitles.LOTTIE_ANIMATION,
      css_config: Constants.defaultBaseCssConfig,
      widgetProperties: {
        textContent: '',
        animationSource: 'Asset URL',
        assetType: 'url',
        assetUrl: 'https://lottie.host/96595b53-e70f-4fc0-9a64-c70a761afa1c/anknM6TrkU.json',
        animationType: 'loop',
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 6,
    cols: 6,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'animation',
} as WorkflowWidgetConfig;

export const notificationWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.NOTIFICATION_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.NOTIFICATION_WIDGET,
    widgetConfigDisplayName: WidgetTitles.NOTIFICATION_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.NOTIFICATION_WIDGET,
      label: WidgetTitles.NOTIFICATION_WIDGET,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        fontSize: 16,
        textFont: 'Mulish',
        textAlignment: 'space-between',
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
        borderWidth: 1,
        borderColor: 'var(--color-border-color)',
        borderStyle: 'solid',
      },
      widgetProperties: {
        textContent: '<p class="m-0">Label here</p>',
        icon: '',
        notificationSettings: {
          channel: '',
          isActive: false,
          category: '',
        },
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'toggle',
} as WorkflowWidgetConfig;

export const currencyAccountsListingWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.CURRENCY_ACCOUNTS_LISTING_WIDGET,
  name: WidgetNames.CURRENCY_ACCOUNTS_LISTING_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.CURRENCY_ACCOUNTS_LISTING_WIDGET,
    widgetConfigDisplayName: WidgetTitles.CURRENCY_ACCOUNTS_LISTING_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.CURRENCY_ACCOUNTS_LISTING_WIDGET,
      label: WidgetTitles.CURRENCY_ACCOUNTS_LISTING_WIDGET,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        fontSize: 16,
        textFont: 'Mulish',
        textAlignment: 'space-between',
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
        borderWidth: 0,
        borderColor: 'var(--color-border-color)',
        borderStyle: 'solid',
        backGroundColor: 'var(--color-surface)',
      },
      widgetProperties: {
        textContent: currencyAccountListItemTemplate,
        horizontalLayoutTemplate: accountListHorizontalItemTemplate,
        horizontalNoRecordsTemplate: 'No records',
        verticalNoRecordsTemplate: 'No records',
        verticalFetchingTemplate: 'Fetching your accounts...',
        horizontalFetchingTemplate: 'Fetching your accounts...',
        icon: 'search',
        inputFieldSettings: {
          isAllowed: true,
          label: 'Search for Accounts',
          isIconAllowed: true,
          css_config: {
            iconSize: 24,
            iconSizeUnit: 'px',
            inputWidth: 100,
            inputPosition: 'start',
          },
        },
        orientation: 'vertical',
        gestures: {
          panEnabled: false,
        },
      },
      contextSwitch: true,
      pagingEnabled: true,
      pageSize: '10',
      ...defaultTableListingConfig,
      viewType: ViewType.LIST,
      columns: baseAccountListingColumns,
      accountContextList: [
        {
          id: AccountStatusType.active,
          name: 'Active',
        },
      ],
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 15,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'listing',
} as WorkflowWidgetConfig;

export const defaultPageConfigurations = { ...commonPageConfigValue };
export const defaultModalConfiguration = {
  modalSizeMarginUnit: 'px',
  modalSizeWidth: 0,
  modalSizeHeight: 0,
  marginBottom: 0,
  closeExternalClick: false,
  closeAction: true,
  closeIconColour: '',
  closeIconUnit: 'px',
  closeIconSize: 32,
  backDropColor: '#000000',
  borderWidthUnit: 'px',
  borderRadiusUnit: 'px',
  borderRadiusTopLeft: 0,
  borderRadiusTopRight: 0,
  borderRadiusBottomLeft: 0,
  borderRadiusBottomRight: 0,
  shadowColour: '',
  shadowBlur: 0,
  shadowSpread: 0,
  shadowOffsetX: 0,
  shadowOffsetY: 0,
  url: '',
  pageType: 'dynamicGrid',
  isRemovable: true,
  mainComponentPageName: 'PageAsModalComponent',
} as ModalConfiguration;

export const switchNavigatorCss = {
  textAlignment: 'center',
  verticalAlignment: 'center',
  textFont: 'var(--font-family)',
  fontSize: 15,
  shadowBlur: 0,
  shadowSpread: 0,
  shadowOffsetX: 0,
  shadowOffsetY: 0,
  borderWidth: 1,
  borderRadiusUnit: 'px',
  borderRadiusTopLeft: 10,
  borderRadiusBottomLeft: 10,
  borderRadiusTopRight: 10,
  borderRadiusBottomRight: 10,
  borderColor: 'var(--color-border-color)',
  paddingUnit: 'px',
  paddingTop: 10,
  paddingLeft: 10,
  paddingRight: 10,
  paddingBottom: 10,
  color: 'var(--color-background)',
  backGroundColor: 'var(--color-primary)',
  disabledBgColor: 'var(--color-background)',
  disabledTextColor: 'var(--color-on-background-muted)',
};

export const fileUploadWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.FILE_UPLOAD_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.FILE_UPLOAD_WIDGET,
    widgetConfigDisplayName: WidgetTitles.FILE_UPLOAD_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.FILE_UPLOAD_WIDGET,
      uploadType: 'image',
      selectedEntityOption: ENTITY_TYPE.FACILITY,
      css_config: {
        ...buttonCssConfig,
        color: 'var(--color-background)',
        backGroundColor: 'var(--color-primary)',
        textAlignment: 'center',
        borderRadiusUnit: 'px',
        borderRadiusTopLeft: 8,
        borderRadiusBottomLeft: 8,
        borderRadiusTopRight: 8,
        borderRadiusBottomRight: 8,
        paddingUnit: 'px',
        paddingTop: 20,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
      },
      widgetProperties: {
        textContent: '',
        icon: 'upload',
      },
      pageIndex: 0,
      zone: '',
      iconConfig: {
        leftIcon: '',
        rightIcon: '',
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 15,
    minItemRows: 20,
    rows: 20,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'generic-widget',
} as WorkflowWidgetConfig;

export const verificationOrganisationCompanyNameWidget: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.VERIFICATION_ORGANISATION_COMPANY_NAME,
  name: WidgetNames.VERIFICATION_ORGANISATION_COMPANY_NAME,
  widgetConfiguration: {
    isRemovable: false,
    widgetDisplayName: 'Company Name',
    widgetConfigDisplayName: WidgetTitles.VERIFICATION_ORGANISATION_COMPANY_NAME,
    attributes: {
      widgetProperties: {
        textContent: '',
        inputLabel: 'Company Name',
      },

      field_attributes: [
        {
          name: 'organisation_companyName',
          decision_config: { required: true },
          label: 'Company Name',
          type: 'text',
          options: [],
          additional_config: {},
        },
      ],
      elementsConfig: [],
      name: WidgetNames.VERIFICATION_ORGANISATION_COMPANY_NAME,
      label: WidgetTitles.VERIFICATION_ORGANISATION_COMPANY_NAME,
      css_config: {
        ...Constants.defaultBaseCssConfig,
      },
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 1,
    rows: 5,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'generic-widget',
};

export const editAddressWidget: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.EDIT_ADDRESS_WIDGET,
  name: WidgetNames.EDIT_ADDRESS_WIDGET,
  widgetConfiguration: {
    isRemovable: false,
    widgetDisplayName: WidgetTitles.ADDRESS_WIDGET,
    widgetConfigDisplayName: WidgetTitles.ADDRESS_WIDGET,
    attributes: {
      css_config: Constants.defaultBaseCssConfig,
      widgetProperties: {
        textContent: '',
        addressLabel: 'Address',
        cityLabel: 'City',
        stateRegionLabel: 'State/Region',
        countryLabel: 'Country',
        zipPostalLabel: 'ZIP/Postal Code',
        inputFieldSettings: {
          isIconAllowed: false,
          css_config: defaultInputCssConfig,
        },
      },
      elementsConfig: [],
    },
  },
  gridPosition: {
    x: 0,
    y: 4,
    rows: 50,
    cols: 15,
    dragEnabled: true,
    resizeEnabled: true,
  },
  widgetIconPath: 'form-widget',
};

export const landingAccountsListingWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.LANDING_ACCOUNTS_LISTING,
  name: WidgetNames.LANDING_ACCOUNTS_LISTING,
  widgetConfiguration: {
    widgetConfigDisplayName: 'Accounts Listing',
    widgetDisplayName: WidgetTitles.LANDING_ACCOUNTS_LISTING,
    attributes: {
      navigationOption: 'use-as-navigation',
      role: DummyRole.EMPTY_ROLE,
      zone: APP_ZONES.LANDING,
      pageUrl: 'home',
      elementsConfig: [],
      widgetProperties: {
        textContent: accountListingForSelectAccountTemplate,
        horizontalLayoutTemplate: accountListHorizontalItemTemplate,
        horizontalNoRecordsTemplate: 'No records',
        verticalNoRecordsTemplate: 'No records',
        verticalFetchingTemplate: 'Fetching your accounts...',
        horizontalFetchingTemplate: 'Fetching your accounts...',
        inputFieldSettings: {
          isAllowed: true,
          isIconAllowed: true,
          css_config: {
            iconSize: 24,
            iconSizeUnit: 'px',
            inputWidth: 100,
            inputPosition: 'start',
          } as InputCssConfig,
          label: 'Search for Accounts',
        },
        icon: 'search',
        orientation: 'vertical',
        menuOptions: [
          {
            label: 'Convert Currency',
            value: 'convert-currency',
            isIdNotExist: '',
            zoneToNavigate: APP_ZONES.PAYMENT,
            urlToNavigate: 'exchange-currency-buy',
          },
          {
            label: 'View Details',
            value: 'view-details',
            isIdNotExist: '',
            zoneToNavigate: APP_ZONES.ACCOUNTS_ZONE,
            urlToNavigate: 'account-credentials',
          },
        ],
        gestures: {
          panEnabled: false,
        },
      },
      ...defaultTableListingConfig,
      viewType: ViewType.LIST,
      columns: accountsColumnListing,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        backGroundColor: 'var(--color-surface)',
        borderWidth: 1,
        borderColor: 'var(--color-border-color)',
        borderStyle: 'solid',
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    dragEnabled: true,
    resizeEnabled: true,
    x: 0,
    y: 3,
    rows: 25,
    cols: 15,
  },
  widgetIconPath: 'listing',
};

export const accountsContextDropdownWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: WidgetNames.ACCOUNTS_CONTEXT_DROPDOWN,
  name: WidgetNames.ACCOUNTS_CONTEXT_DROPDOWN,
  widgetConfiguration: {
    widgetConfigDisplayName: 'Account Context Drop-down',
    widgetDisplayName: WidgetTitles.ACCOUNTS_CONTEXT_DROPDOWN,
    attributes: {
      role: DummyRole.EMPTY_ROLE,
      zone: APP_ZONES.LANDING,
      elementsConfig: [],
      textContent: 'Accounts',
      iconConfig: {
        leftIcon: 'change-pin',
        rightIcon: '',
      },
      enableTotalWealth: false,
      showFlag: true,
      enableAccountContextSwitch: true,
      widgetVisualTemplate: accountSwitchWidgetVisualTemplate,
      widgetDropdownTemplate: accountSwitchDropdownTemplate,
      accountContextList: [
        {
          id: AccountStatusType.active,
          name: 'Active',
        },
      ],
      widgetProperties: {
        textContent: '',
        inputFieldSettings: {
          isIconAllowed: false,
          companyNameLabel: '',
          css_config: { ...defaultInputCssConfig, inputHeight: undefined },
        },
      },
      css_config: {
        ...Constants.defaultBaseCssConfig,
        backgroundColor: 'transparent',
        inactiveBorderColor: 'var(--color-on-surface-muted)',
        inactiveBackgroundColor: '',
        verticalAlignment: 'center',
        borderColor: 'var(--color-border-color)',
        borderStyle: 'solid',
        borderWidth: 1,
        height: 80,
        borderRadiusBottomRight: 10,
        borderRadiusBottomLeft: 10,
        borderRadiusTopRight: 10,
        borderRadiusTopLeft: 10,
        display: 'flex',
        paddingLeft: 10,
        paddingRight: 10,
        fontSize: 18,
        isTextBold: true,
        justifyContent: 'space-between',
        textAlignment: 'left',
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    dragEnabled: true,
    resizeEnabled: true,
    x: 0,
    y: 6,
    rows: 3,
    cols: 15,
  },
  widgetIconPath: 'input-field-widget',
};

export const desktopBulkPaymentListingWidgetConfig = createDesktopListWidget(
  WidgetNames.DESKTOP_BULK_PAYMENT_LISTING_WIDGET,
  WidgetTitles.DESKTOP_BULK_PAYMENT_LISTING_WIDGET,
  'bulk-payment-listing',
  'Bulk Payment Listing Widget',
  ['05', '10', '20'],
  'p-datatable-lg',
);
export const desktopBulkPaymentHistoryListingWidgetConfig = createDesktopListWidget(
  WidgetNames.DESKTOP_BULK_PAYMENT_HISTORY_LISTING_WIDGET,
  WidgetTitles.DESKTOP_BULK_PAYMENT_HISTORY_LISTING_WIDGET,
  'bulk-payment-history',
  'Bulk Payment History Listing Widget',
  ['05', '10', '20'],
  'p-datatable-lg',
);

function createDesktopListWidget(
  zone: string,
  title: string,
  entity: string,
  displayName: string,
  selectedPaginationOption: string[],
  selectTableSize?: string,
) {
  return {
    widgetTypeConfig: 'new',
    uniqueId: cryptoRandomUUID(),
    name: zone,
    widgetConfiguration: {
      widgetDisplayName: displayName,
      widgetConfigDisplayName: title,
      attributes: {
        elementsConfig: [],
        name: zone,
        css_config: {
          ...buttonCssConfig,
          color: 'rgba(251, 194, 94, 1)',
          borderWidth: 1,
          borderColor: 'var(--color-border-color)',
          borderStyle: 'solid',
        },
        widgetProperties: {
          textContent: '',
          zoneToNavigate: '',
          urlToNavigate: '',
          entityInformation: {
            entity: entity,
            dateFromKey: 'start',
            dateToKey: 'end',
          },
          inputFieldSettings: {
            ...inputSettings,
            css_config: {
              inputWidth: 25,
              iconSize: 24,
              iconSizeUnit: 'px',
              inputPosition: 'end',
            } as InputCssConfig,
            label: 'Search',
            type: 'search',
            isIconAllowed: true,
            isAllowed: false,
          },
        },
        pageIndex: 0,
        zone: '',
        iconConfig: {
          leftIcon: '',
          rightIcon: '',
        },
        rowSettings: {
          rowBgColor: 'var(--color-surface)',
          oddRowBgColor: 'var(--color-surface)',
          evenRowBgColor: 'var(--color-surface)',
          showErrorDuplicationRowBgColor: false,
        },
        selectedListOption: '',
        viewListOption: '',
        tableGridSetting: {
          showHorizontalGridLines: true,
          showVerticalGridLines: false,
          gridLinesWidth: 1,
        },
        viewPaginationOption: selectedPaginationOption,
        viewTableSize: selectTableSize,
        tableHeaderCustomCss:
          'font-size: 16px; font-weight: 700; color:var(--color-on-surface-muted); padding:15px 10px 15px 24px',
        tableRowCustomCss: `padding: 1.6rem 2.4rem; font-size: 16px; font-weight: 400;`,
        tableToolbarHeaderBgColor: 'var(--color-surface)',
        columns: [],
      },
      isRemovable: true,
    },
    gridPosition: {
      x: 0,
      y: 7,
      minItemCols: 15,
      minItemRows: 30,
      rows: 30,
      cols: 39,
      resizeEnabled: true,
      dragEnabled: true,
    },
    widgetIconPath: 'listing',
  } as WorkflowWidgetConfig;
}

export const conditionalWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.CONDITIONAL_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.CONDITIONAL_WIDGET,
    widgetConfigDisplayName: WidgetTitles.CONDITIONAL_WIDGET,
    nestedWidgets: conditionalNestedWidgets,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.CONDITIONAL_WIDGET,
      type: 'links',
      slug: '',
      label: '',
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'left',
        verticalAlignment: 'center',
        textFont: 'Metropolis',
        fontSize: 20,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        backGroundColor: '#fff',
        color: '#384F80',
      },
      widgetProperties: {
        textContent: '',
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 1,
    minItemCols: 1,
    minItemRows: 1,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: 'conditional',
} as WorkflowWidgetConfig;

export const travelEmployeeListingColumns = [
  {
    label: 'Name',
    value: 'firstName',
    handleBarKey: '<b>{{firstName }} {{lastName }}</b>',
    selected: true,
  },
  {
    label: 'Position',
    value: 'position',
    handleBarKey: '{{extraAttributes.position}}',
    selected: true,
  },
  {
    label: 'Events Linked',
    value: 'events',
    handleBarKey: '{{#if eventsLinked.length}} {{eventsLinked.0.name}} +{{eventsLinked.length}} {{/if}}',
    selected: true,
  },
  {
    label: 'Phone Number',
    value: 'phoneNumber',
    handleBarKey: '{{phoneNumber}}',
    selected: true,
  },
  {
    label: 'Email',
    value: `email'`,
    handleBarKey: '{{ email}}',
    selected: true,
  },
];

export const bulkpaymentListing = [
  {
    label: 'Row',
    value: 'row',
    handleBarKey: '<b>{{row }} </b>',
    selected: true,
  },
  {
    label: 'Recipient / Org. Name',
    value: 'name',
    handleBarKey: '{{name}}',
    selected: true,
  },
  {
    label: 'Sort Code',
    value: 'beneficiaryBankAccounts.0.sort_code',
    handleBarKey: '{{beneficiaryBankAccounts.0.sort_code}}',
    selected: true,
  },
  {
    label: 'A/C Number',
    value: 'beneficiaryBankAccounts.0.account_number',
    handleBarKey: '{{beneficiaryBankAccounts.0.account_number}}',
    selected: true,
  },
  {
    label: 'Type',
    value: `type`,
    handleBarKey: '<span class="text-capitalize"> {{type}}</span>',
    selected: true,
  },
  {
    label: 'Amount',
    value: `amount`,
    handleBarKey: '{{ currencyPipe amount currencyCode=currency }} ',
    selected: true,
  },
  {
    label: 'Schedule',
    value: `scheduleType`,
    handleBarKey: '<span class="text-capitalize"> {{ scheduleType}} </span>',
    selected: true,
  },
];

export const backNextButtonWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.ONBOARDING_BACK_NEXT_BUTTON,
  widgetConfiguration: {
    widgetConfigDisplayName: 'Button',
    widgetDisplayName: WidgetTitles.ONBOARDING_BACK_NEXT_BUTTON,
    attributes: {
      elementsConfig: [],
      textContent: 'button',
      widgetProperties: {
        textContent: '',
        icon: '',
      },
      css_config: {
        ...Constants.defaultBaseCssConfig,
        backGroundColor: 'var(--color-primary)',
        color: 'var(--color-background)',
        textAlignment: 'center',
        verticalAlignment: 'center',
        paddingTop: 20,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    dragEnabled: true,
    resizeEnabled: true,
    x: 0,
    y: 6,
    rows: 3,
    cols: 15,
  },
  widgetIconPath: 'forward-back-navigation',
};

export function getBackNextButton(override: {
  icon: string;
  gridPosition: GridPosition;
  displayText: string;
  buttonType: 'next' | 'back';
  text?: string;
  shape?: 'rectangle' | 'circle' | 'square';
  styles?: any;
}) {
  return new OnboardingNextBackWidgetBuilder()
    .configDisplayName(override.displayText)
    .attributes({
      widgetProperties: {
        textContent: override.text ?? '',
        icon: override.icon,
        backgroundShape: override.shape ?? 'circle',
      } as BaseWidgetProperties,
      buttonType: override.buttonType,
    } as unknown as WorkflowWidgetConfigurationAttributes)
    .gridPosition(override.gridPosition)
    .cssConfig(override?.styles)
    .build();
}

export const uploadFileWithPopupWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.UPLOAD_FILE_WITH_POPUP_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.UPLOAD_FILE_WITH_POPUP_WIDGET,
    widgetConfigDisplayName: WidgetTitles.UPLOAD_FILE_WITH_POPUP_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.UPLOAD_FILE_WITH_POPUP_WIDGET,
      uploadType: 'image',
      css_config: {
        ...buttonCssConfig,
        color: 'var(--color-background)',
        backGroundColor: 'var(--color-primary)',
        textAlignment: 'center',
        borderRadiusUnit: 'px',
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
        paddingUnit: 'px',
        paddingTop: 20,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
        fontSize: 16,
      },
      widgetProperties: {
        textContent: '',
        formKey: '',
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 15,
    minItemRows: 13,
    rows: 13,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
    layerIndex: 1,
  },
  widgetIconPath: 'generic-widget',
} as WorkflowWidgetConfig;

// Function to create a nested widget config
export const createNestedWidgetConfig = (
  name: string,
  displayName: string,
  cssConfig: object,
  widgetProperties = {} as BaseWidgetProperties,
) =>
  ({
    name,
    widgetConfigDisplayName: displayName,
    attributes: {
      widgetProperties: {
        ...widgetProperties,
      },
      elementsConfig: [],
      css_config: cssConfig,
    },
  }) as NestedWorkflowWidgetConfiguration;

export const cardsWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.CARDS_WIDGET,
  widgetConfiguration: {
    isRemovable: true,
    widgetDisplayName: WidgetTitles.CARDS_WIDGET,
    widgetConfigDisplayName: WidgetTitles.CARDS_WIDGET,
    nestedWidgets: cardsNestedWidgets,
    attributes: {
      cardLayoutType: 'individual',
      sortBy: 'date-added-newest-first',
      css_config: { ...Constants.defaultBaseCssConfig, backGroundColor: '' },

      widgetProperties: {
        textContent: 'textContent',
        subHeadingLabel: '',
      },
      elementsConfig: [],
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    rows: 7,
    cols: 15,
    dragEnabled: true,
    resizeEnabled: true,
  },
  widgetIconPath: 'card',
};

export const cardSensitiveDetailsConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: Math.floor(Math.random() * (1000000000 + 1)),
  name: WidgetNames.CARD_SENSITIVE_DETAILS,
  widgetConfiguration: {
    isRemovable: true,
    widgetDisplayName: WidgetTitles.CARD_SENSITIVE_DETAILS,
    widgetConfigDisplayName: WidgetTitles.CARD_SENSITIVE_DETAILS,
    attributes: {
      css_config: {
        ...Constants.defaultBaseCssConfig,
        backGroundColor: '',
        iconColor: 'var(--color-on-background-muted)',
        iconSize: 24,
        fontSize: 16,
        fontUnit: 'px',
        iconUnit: 'px',
      },

      widgetProperties: {
        textContent: '',
        subHeadingLabel: '',
        icon: 'mat_copy',
        labelTextColor: 'var(--color-on-background-muted)',
        detailsTextColor: 'var(--color-on-background)',
      },
      elementsConfig: [],
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    rows: 7,
    cols: 15,
    dragEnabled: true,
    resizeEnabled: true,
  },
  widgetIconPath: 'generic-widget',
};

export const desktopPaymentRequestPendingListingWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.DESKTOP_PAYMENT_REQUEST_PENDING_APPROVED_LISTING_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetNames.DESKTOP_PAYMENT_REQUEST_PENDING_APPROVED_LISTING_WIDGET,
    widgetConfigDisplayName: WidgetTitles.DESKTOP_PAYMENT_REQUEST_PENDING_APPROVED_LISTING_WIDGET,
    nestedWidgets: {
      actionDropDown: {
        name: WidgetNames.DROPDOWN,
        widgetConfigDisplayName: 'Approval Selection Dropdown',
        isRemovable: true,
        attributes: {
          widgetProperties: {
            textContent: '',
          },
          elementsConfig: [],
          css_config: {
            ...Constants.defaultUiNestedButtonCssConfig,
            backGroundColor: 'var(--color-background)',
            color: 'var(--color-on-background)',
            borderWidth: 1,
            borderColor: 'var(--color-border-color)',
            marginLeft: 10,
            paddingTop: 3,
            paddingBottom: 3,
            textAlignment: 'left',
            verticalAlignment: 'center',
          },
        },
      },
      actionConfirmButton: {
        name: WidgetNames.BUTTON,
        widgetConfigDisplayName: 'Confirm Button',
        isRemovable: true,
        attributes: {
          widgetProperties: {
            textContent: 'Confirm',
          },
          elementsConfig: [],
          css_config: {
            ...Constants.defaultUiNestedButtonCssConfig,
            verticalAlignment: 'center',
            borderRadiusTopLeft: 15,
            borderRadiusBottomLeft: 15,
            borderRadiusTopRight: 15,
            borderRadiusBottomRight: 15,
            paddingRight: 20,
            paddingLeft: 20,
          },
        },
      },
    },
    attributes: {
      elementsConfig: [],
      name: WidgetNames.DESKTOP_PAYMENT_REQUEST_PENDING_APPROVED_LISTING_WIDGET,
      css_config: {
        ...Constants.defaultBaseCssConfig,
        textAlignment: 'center',
        verticalAlignment: 'center',
        color: 'var(--color-on-background-muted)',
        justifyContent: 'left',
        fontSize: 16,
        borderWidth: 1,
        borderColor: 'var(--palette-color-2)',
        backGroundColor: 'var(--color-background)',
        borderStyle: 'solid',
        borderRadiusUnit: 'px',
        borderRadiusTopLeft: 5,
        borderRadiusBottomLeft: 5,
        borderRadiusTopRight: 5,
        borderRadiusBottomRight: 5,
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        iconSize: 24,
      },
      widgetProperties: {
        icon: 'search',
        textContent: '',
        showEntity: true,
        entityInformation: {
          entity: EntityType.PaymentRequest,
        },
        inputFieldSettings: {
          ...inputSettings,
          css_config: {
            inputWidth: 25,
            iconSize: 24,
            iconSizeUnit: 'px',
            inputPosition: 'end',
          } as InputCssConfig,
          label: 'Search',
          type: 'search',
          isIconAllowed: true,
          isAllowed: false,
        },
        dropdownActionSetting: {
          isAllowDropdownAction: true,
          dropdownLabel: '',
          dropdownWidth: 25,
        },
      },
      pageSize: 10,
      selectedListOption: 'None',
      viewListOption: 'none',
      tableHeaderSetting: {
        css_config: defaultTableHeaderSettingCssConfig,
      },
      rowSettings: {
        rowBgColor: 'var(--color-background)',
        showErrorDuplicationRowBgColor: false,
      },
      tableGridSetting: {
        showHorizontalGridLines: true,
        showVerticalGridLines: false,
        gridLinesWidth: 1,
      },
      tableCheckboxSettings: {
        enableCheckbox: true,
        inputFieldSettings: { ...defaultTableCheckBoxCssConfig },
      },
      viewTableSize: 'p-datatable-lg',
      viewPaginationOption: ['05', '10', '20'],
      tableToolbarHeaderBgColor: 'var(--color-surface)',
      columns: [],
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 1,
    y: 7,
    rows: 13,
    cols: 39,
    resizeEnabled: true,
    dragEnabled: true,
  },
  widgetIconPath: '',
} as WorkflowWidgetConfig;

export const secondaryIdentifierWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.SECONDARY_IDENTIFIER,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.SECONDARY_IDENTIFIER,
    widgetConfigDisplayName: WidgetTitles.SECONDARY_IDENTIFIER,
    nestedWidgets: {
      continueButton: {
        name: WidgetNames.BUTTON,
        widgetConfigDisplayName: 'Continue Button',
        attributes: {
          widgetProperties: {
            textContent: 'Continue',
            globalStyling: true,
            globalStylingClass: `${ButtonTypes.PRIMARY} ${ButtonSizeOptions.FULL_WIDTH}`,
          },
          elementsConfig: [],
          css_config: {
            ...Constants.defaultBaseCssConfig,
          },
        },
      },
    },
    attributes: {
      elementsConfig: [],
      name: WidgetNames.SECONDARY_IDENTIFIER,
      css_config: {},
      widgetProperties: {
        textContent: '',
        formKey: '',
      },
      pageIndex: 0,
      zone: '',
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 0,
    minItemCols: 1,
    minItemRows: 5,
    rows: 2,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
    layerIndex: 0,
  },
  widgetIconPath: '',
} as WorkflowWidgetConfig;

export const cardPinWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.CARD_PIN,
  widgetConfiguration: {
    isRemovable: true,
    widgetDisplayName: WidgetTitles.CARD_PIN,
    widgetConfigDisplayName: WidgetTitles.CARD_PIN,
    attributes: {
      css_config: { ...Constants.defaultBaseCssConfig },
      widgetProperties: {
        textContent: 'textContent',
      },
      elementsConfig: [],
    },
  },
  gridPosition: {
    x: 0,
    y: 0,
    rows: 7,
    cols: 15,
    dragEnabled: true,
    resizeEnabled: true,
  },
  widgetIconPath: '',
};

export const paymentRailSelectorWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.PAYMENT_RAIL_SELECTOR_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.PAYMENT_RAIL_SELECTOR_WIDGET,
    widgetConfigDisplayName: WidgetTitles.PAYMENT_RAIL_SELECTOR_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.PAYMENT_RAIL_SELECTOR_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: '',
        inputLabel: 'Payment Rail',
      },
    },
    isRemovable: true,
  },
  gridPosition: {
    x: 0,
    y: 18,
    minItemCols: 1,
    minItemRows: 5,
    rows: 3,
    cols: 15,
    resizeEnabled: true,
    dragEnabled: true,
    layerIndex: 0,
  },
  widgetIconPath: '',
} as WorkflowWidgetConfig;

export const beneficiaryTypeWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.BENEFICIARY_TYPE_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.BENEFICIARY_TYPE_WIDGET,
    widgetConfigDisplayName: WidgetTitles.BENEFICIARY_TYPE_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.BENEFICIARY_TYPE_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: '',
        inputLabel: 'Type',
        inputFieldConfig: {
          id: 'type',
          type: 'select',
          label: 'Type',
          parentForm: FormID.BENEFICIARY_FORM,
          name: 'type',
          validation: {
            required: true,
          },
          inputFormat: InputFormatType.DROPDOWN,
          inputPlaceholder: 'Select Type',
          options: [
            {
              label: 'Personal',
              value: 'personal',
            },
            {
              label: 'Business',
              value: 'business',
            },
          ],
        },
      },
    },
    isRemovable: true,
  },
  gridPosition: undefined,
  widgetIconPath: 'input-field-widget',
} as WorkflowWidgetConfig;

export const beneficiaryCountryCurrencyWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.BENEFICIARY_COUNTRY_CURRENCY_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.BENEFICIARY_COUNTRY_CURRENCY_WIDGET,
    widgetConfigDisplayName: WidgetTitles.BENEFICIARY_COUNTRY_CURRENCY_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.BENEFICIARY_COUNTRY_CURRENCY_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: '',
        countryLabel: 'Country',
        currencyLabel: 'Currency',
        showCurrencyFlags: true,
        isCountryVisible: true,
        isCurrencyVisible: true,
        defaultBeneficiaryCountry: '',
        defaultBeneficiaryCurrency: '',
      },
    },
    isRemovable: true,
  },
  gridPosition: undefined,
  widgetIconPath: 'form-widget',
} as WorkflowWidgetConfig;

export const beneficiaryCredentialsWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.BENEFICIARY_CREDENTIALS_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.BENEFICIARY_CREDENTIALS_WIDGET,
    widgetConfigDisplayName: WidgetTitles.BENEFICIARY_CREDENTIALS_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.BENEFICIARY_CREDENTIALS_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: '',
      },
      childComponents: [
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Nickname',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Nickname',
              inputFieldConfig: {
                id: 'nickName',
                type: 'text',
                label: 'Nickname',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'nickName',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Full Name',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Full Name',
              inputFieldConfig: {
                id: 'fullName',
                type: 'text',
                label: 'Full Name',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'fullName',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
                validation: {
                  required: true,
                },
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Account Number',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Account Number',
              inputFieldConfig: {
                id: 'accountNumber',
                type: 'text',
                label: 'Account Number',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'accountNumber',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Sort Code',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Sort Code',
              inputFieldConfig: {
                id: 'sortCode',
                type: 'text',
                label: 'Sort Code',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'sortCode',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'IBAN',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'IBAN',
              inputFieldConfig: {
                id: 'iban',
                type: 'text',
                label: 'IBAN',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'iban',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'BIC Code',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'BIC Code',
              inputFieldConfig: {
                id: 'BIC',
                type: 'text',
                label: 'BIC Code',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'BIC',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
              css_config: {},
            },
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'RTN Canada',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'RTN Canada',
              inputFieldConfig: {
                id: 'rtnCanada',
                type: 'text',
                label: 'RTN Canada',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'rtnCanada',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'BSB Code',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'BSB Code',
              inputFieldConfig: {
                id: 'bsbCode',
                type: 'text',
                label: 'BSB Code',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'bsbCode',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'CNAPS',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'CNAPS',
              inputFieldConfig: {
                id: 'cnaps',
                type: 'text',
                label: 'CNAPS',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'cnaps',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Bank Name',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Bank Name',
              inputFieldConfig: {
                id: 'bankName',
                type: 'text',
                label: 'Bank Name',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'bankName',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Bank ID',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Bank ID',
              inputFieldConfig: {
                id: 'bankId',
                type: 'text',
                label: 'Bank ID',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'bankId',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Bank Code',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Bank Code',
              inputFieldConfig: {
                id: 'bankCode',
                type: 'text',
                label: 'Bank Code',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'bankCode',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Branch Code',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Branch Code',
              inputFieldConfig: {
                id: 'branchCode',
                type: 'text',
                label: 'Branch Code',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'branchCode',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'IFSC',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'IFSC',
              inputFieldConfig: {
                id: 'ifsc',
                type: 'text',
                label: 'IFSC',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'ifsc',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'CLABE',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'CLABE',
              inputFieldConfig: {
                id: 'clabe',
                type: 'text',
                label: 'CLABE',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'clabe',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'ABA Number',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'ABA Number',
              inputFieldConfig: {
                id: 'aba',
                type: 'text',
                label: 'ABA Number',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'aba',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
      ],
    },
    isRemovable: true,
  },
  gridPosition: undefined,
  widgetIconPath: 'form-widget',
} as WorkflowWidgetConfig;

export const beneficiaryAddressWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.BENEFICIARY_ADDRESS_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.BENEFICIARY_ADDRESS_WIDGET,
    widgetConfigDisplayName: WidgetTitles.BENEFICIARY_ADDRESS_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.BENEFICIARY_ADDRESS_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: '',
      },
      childComponents: [
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Country',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Country',
              inputFieldConfig: {
                id: 'country',
                type: 'text',
                label: 'Country',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'country',
                inputFormat: InputFormatType.DROPDOWN,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'State/Province/Region',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'State/Province/Region',
              inputFieldConfig: {
                id: 'stateProvinceRegion',
                type: 'text',
                label: 'State/Province/Region',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'stateProvinceRegion',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'City',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'City',
              inputFieldConfig: {
                id: 'city',
                type: 'text',
                label: 'City',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'city',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Address Line 1',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Address Line 1',
              inputFieldConfig: {
                id: 'addressLine1',
                type: 'text',
                label: 'Address Line 1',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'addressLine1',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Address Line 2',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Address Line 2',
              inputFieldConfig: {
                id: 'addressLine2',
                type: 'text',
                label: 'Address Line 2',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'addressLine2',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Address Line 3',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Address Line 3',
              inputFieldConfig: {
                id: 'addressLine3',
                type: 'text',
                label: 'Address Line 3',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'addressLine3',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Address Line 4',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Address Line 4',
              inputFieldConfig: {
                id: 'addressLine4',
                type: 'text',
                label: 'Address Line 4',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'addressLine4',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'House Number',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'House Number',
              inputFieldConfig: {
                id: 'houseNumber',
                type: 'text',
                label: 'House Number',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'houseNumber',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Postal Code',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Postal Code',
              inputFieldConfig: {
                id: 'postalCode',
                type: 'text',
                label: 'Postal Code',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'postalCode',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
        {
          name: WidgetNames.INPUT_WIDGET,
          widgetConfigDisplayName: 'Refinement',
          attributes: {
            elementsConfig: [],
            widgetProperties: {
              textContent: '',
              inputLabel: 'Refinement',
              inputFieldConfig: {
                id: 'refinement',
                type: 'text',
                label: 'Refinement',
                parentForm: FormID.BENEFICIARY_FORM,
                name: 'refinement',
                inputFormat: InputFormatType.SHORT_TEXT_INPUT,
                inputPlaceholder: '',
              },
            },
            css_config: {},
          },
        },
      ],
    },
    isRemovable: true,
  },
  gridPosition: undefined,
  widgetIconPath: 'form-widget',
} as WorkflowWidgetConfig;

export const beneficiaryPaymentRailWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.BENEFICIARY_PAYMENT_RAIL_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.BENEFICIARY_PAYMENT_RAIL_WIDGET,
    widgetConfigDisplayName: WidgetTitles.BENEFICIARY_PAYMENT_RAIL_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.BENEFICIARY_PAYMENT_RAIL_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: '',
        inputLabel: 'Payment Rail',
        defaultSelectedPaymentRail: {},
        isPaymentRailSelectorVisible: true,
        inputFieldConfig: {
          id: 'paymentRail',
          type: 'select',
          label: 'Payment Rail',
          parentForm: FormID.BENEFICIARY_FORM,
          name: 'paymentRail',
          validation: {
            required: true,
          },
          inputFormat: InputFormatType.DROPDOWN,
          inputPlaceholder: 'Select Payment Rail',
          options: [],
        },
      },
    },
    isRemovable: true,
  },
  gridPosition: undefined,
  widgetIconPath: 'input-field-widget',
} as WorkflowWidgetConfig;

export const airwallexRfiWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.AIRWALLEX_RFI_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.AIRWALLEX_RFI_WIDGET,
    widgetConfigDisplayName: WidgetTitles.AIRWALLEX_RFI_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.AIRWALLEX_RFI_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: 'Additional information is required to complete verification.',
      },
    },
    isRemovable: true,
  },
  gridPosition: undefined,
  widgetIconPath: 'integration',
} as WorkflowWidgetConfig;

export const sumsubWidgetProfileConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.SUMSUB_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.SUMSUB_Title,
    widgetConfigDisplayName: WidgetTitles.SUMSUB_Title,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.SUMSUB_WIDGET,
      css_config: {},
      widgetProperties: {
        textContent: '',
      },
    },
    isRemovable: true,
  },
  gridPosition: undefined,
  widgetIconPath: 'integration',
} as WorkflowWidgetConfig;

export const multiCurrencyWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.MULTI_CURRENCY_WIDGET,
  widgetConfiguration: {
    widgetConfigDisplayName: 'Multi-Currency Currency Selector',
    widgetDisplayName: WidgetTitles.MULTI_CURRENCY_WIDGET,
    attributes: {
      role: DummyRole.EMPTY_ROLE,
      zone: APP_ZONES.LANDING,
      elementsConfig: [],
      widgetProperties: {
        widgetVisualTemplate: multiCurrencyVisualTemplate,
        widgetDropdownTemplate: multiCurrencyDropdownTemplate,
        selectedAvailableCurrencies: [],
        textContent: '',
        inputLabel: 'Select currency',
      },
      css_config: {
        ...Constants.defaultBaseCssConfig,
        backgroundColor: 'transparent',
        inactiveBorderColor: 'var(--color-on-surface-muted)',
        inactiveBackgroundColor: '',
        verticalAlignment: 'center',
        borderColor: 'var(--color-border-color)',
        borderStyle: 'solid',
        borderWidth: 1,
        height: 80,
        borderRadiusBottomRight: 10,
        borderRadiusBottomLeft: 10,
        borderRadiusTopRight: 10,
        borderRadiusTopLeft: 10,
        display: 'flex',
        paddingLeft: 10,
        paddingRight: 10,
        fontSize: 18,
        isTextBold: true,
        justifyContent: 'space-between',
        textAlignment: 'left',
        shadowBlur: 0,
        shadowSpread: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'input-field-widget',
};

export const cloudflareTurnstileWidgetConfig: WorkflowWidgetConfig = {
  widgetTypeConfig: 'new',
  uniqueId: cryptoRandomUUID(),
  name: WidgetNames.CLOUDFLARE_TURNSTILE_WIDGET,
  widgetConfiguration: {
    widgetDisplayName: WidgetTitles.CLOUDFLARE_TURNSTILE_WIDGET,
    widgetConfigDisplayName: WidgetTitles.CLOUDFLARE_TURNSTILE_WIDGET,
    attributes: {
      elementsConfig: [],
      name: WidgetNames.CLOUDFLARE_TURNSTILE_WIDGET,
      css_config: { ...Constants.defaultBaseCssConfig },
      widgetProperties: {
        textContent: '',
      },
    },
    isRemovable: true,
  },
  widgetIconPath: 'integration',
};
